import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import './Reviews.css';

function StarDisplay({ stars }) {
    return (
        <span className="rev-stars">
            {[1, 2, 3, 4, 5].map(i => (
                <span key={i} className={i <= stars ? 'star filled' : 'star empty'}>★</span>
            ))}
        </span>
    );
}

function formatDate(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
}

function ReviewRow({ review }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="rev-row" onClick={() => setExpanded(e => !e)}>
            <div className="rev-row-summary">
                <StarDisplay stars={review.stars} />
                <span className="rev-row-name">{review.displayName}</span>
                <span className="rev-row-date">{formatDate(review.date)}</span>
                <span className={`rev-row-chevron${expanded ? ' open' : ''}`}>›</span>
            </div>
            {expanded && review.comment && (
                <div className="rev-row-comment">{review.comment}</div>
            )}
            {expanded && !review.comment && (
                <div className="rev-row-comment rev-row-comment-empty">Kein Kommentar hinterlassen.</div>
            )}
        </div>
    );
}

export function Reviews() {
    const [summary, setSummary] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [activeFilter, setActiveFilter] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('api/Review/Summary').then(r => r.json()).then(setSummary).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const url = activeFilter != null ? `api/Review?stars=${activeFilter}` : 'api/Review';
        fetch(url).then(r => r.json()).then(data => {
            setReviews(data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [activeFilter]);

    const maxDistCount = summary
        ? Math.max(1, ...Object.values(summary.distribution))
        : 1;

    return (
        <div className="reviews-page">
            <Helmet>
                <title>Bewertungen | ho.li design</title>
            </Helmet>

            <h1 className="reviews-title">Bewertungen</h1>

            {summary && summary.totalCount > 0 && (
                <div className="reviews-overview">
                    <div className="reviews-avg-block">
                        <span className="reviews-avg-number">{summary.averageStars.toFixed(1)}</span>
                        <div>
                            <div className="rev-stars reviews-avg-stars">
                                {[1, 2, 3, 4, 5].map(i => (
                                    <span key={i} className={i <= Math.round(summary.averageStars) ? 'star filled' : 'star empty'}>★</span>
                                ))}
                            </div>
                            <p className="reviews-avg-count">{summary.totalCount} Bewertungen</p>
                        </div>
                    </div>

                    <div className="reviews-distribution">
                        {[5, 4, 3, 2, 1].map(star => (
                            <div key={star} className="reviews-dist-row">
                                <span className="reviews-dist-label">{star} ★</span>
                                <div className="reviews-dist-bar-wrap">
                                    <div
                                        className="reviews-dist-bar"
                                        style={{ width: `${(summary.distribution[star] / maxDistCount) * 100}%` }}
                                    />
                                </div>
                                <span className="reviews-dist-count">{summary.distribution[star]}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {summary && summary.totalCount > 0 && (
                <div className="reviews-filter">
                    <button
                        className={`reviews-filter-btn${activeFilter === null ? ' active' : ''}`}
                        onClick={() => setActiveFilter(null)}
                    >
                        Alle
                    </button>
                    {[5, 4, 3, 2, 1].map(s => (
                        <button
                            key={s}
                            className={`reviews-filter-btn${activeFilter === s ? ' active' : ''}`}
                            onClick={() => setActiveFilter(activeFilter === s ? null : s)}
                        >
                            {s} ★
                        </button>
                    ))}
                </div>
            )}

            {!loading && reviews.length === 0 && (
                <p className="reviews-empty">Keine Bewertungen gefunden.</p>
            )}

            {reviews.length > 0 && (
                <div className="reviews-list">
                    {reviews.map(r => <ReviewRow key={r.id} review={r} />)}
                </div>
            )}
        </div>
    );
}
